import {
  createOffsetPaginatedSchema,
  KitBasketEntryDtoSchema,
  KitCorrectionDtoSchema,
  KitDemandForecastResponseSchema,
  KitOutDtoSchema,
  KitOutProtocolWithItemsDtoSchema,
  KitShipmentDtoSchema,
  KitStockItemDtoSchema,
  LabKitDtoSchema,
  LabKitExpirationDtoSchema,
  TrialLaboratoryDtoSchema,
  VisitKitsResponseSchema,
  type SaveVisitKitsRequest,
  type VisitKitsResponse,
  type AddKitOutRequest,
  type CreateKitCorrectionRequest,
  type CreateKitOutProtocolRequest,
  type CreateKitShipmentRequest,
  type CreateLabKitExpirationRequest,
  type CreateLabKitRequest,
  type CreateTrialLaboratoryRequest,
  type KitBasketEntryDto,
  type KitBasketQuery,
  type KitCorrectionDto,
  type KitCorrectionListQuery,
  type KitDemandForecastItemDto,
  type KitDemandForecastQuery,
  type KitOutDto,
  type KitOutProtocolListQuery,
  type KitOutProtocolWithItemsDto,
  type KitShipmentDto,
  type KitShipmentListQuery,
  type KitStockItemDto,
  type KitStockQuery,
  type LabKitDto,
  type LabKitExpirationDto,
  type LabKitListQuery,
  type OffsetPaginated,
  type TrialLaboratoryDto,
  type TrialLaboratoryListQuery,
  type UpdateLabKitRequest,
  type UpdateTrialLaboratoryRequest,
} from '@ctms/contracts';
import { z } from 'zod';

import { apiClient } from '../../api/client.js';
import { API_PATHS } from '../../api/paths.js';

const TrialLaboratoryListResponseSchema = createOffsetPaginatedSchema(TrialLaboratoryDtoSchema);
const LabKitListResponseSchema = createOffsetPaginatedSchema(LabKitDtoSchema);
const LabKitExpirationListResponseSchema = z.array(LabKitExpirationDtoSchema);
const KitShipmentListResponseSchema = createOffsetPaginatedSchema(KitShipmentDtoSchema);
const KitStockListResponseSchema = z.array(KitStockItemDtoSchema);
const KitBasketListResponseSchema = z.array(KitBasketEntryDtoSchema);
const KitOutProtocolListResponseSchema = createOffsetPaginatedSchema(KitOutProtocolWithItemsDtoSchema);
const KitCorrectionListResponseSchema = createOffsetPaginatedSchema(KitCorrectionDtoSchema);

function toLaboratoriesSearchParams(query: TrialLaboratoryListQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(query.page));
  searchParams.set('limit', String(query.limit));
  if (query.trialId !== undefined) {
    searchParams.set('trialId', String(query.trialId));
  }
  if (query.search && query.search.trim().length > 0) {
    searchParams.set('search', query.search.trim());
  }
  return searchParams;
}

function toKitsSearchParams(query: LabKitListQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(query.page));
  searchParams.set('limit', String(query.limit));
  if (query.trialLaboratoryId !== undefined) {
    searchParams.set('trialLaboratoryId', String(query.trialLaboratoryId));
  }
  if (query.search && query.search.trim().length > 0) {
    searchParams.set('search', query.search.trim());
  }
  return searchParams;
}

function toShipmentsSearchParams(query: KitShipmentListQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('trialId', String(query.trialId));
  searchParams.set('page', String(query.page));
  searchParams.set('limit', String(query.limit));
  if (query.search && query.search.trim().length > 0) {
    searchParams.set('search', query.search.trim());
  }
  return searchParams;
}

function toStockSearchParams(query: KitStockQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('trialId', String(query.trialId));
  if (query.trialLaboratoryId !== undefined) {
    searchParams.set('trialLaboratoryId', String(query.trialLaboratoryId));
  }
  return searchParams;
}

function toDemandForecastSearchParams(query: KitDemandForecastQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('trialId', String(query.trialId));
  searchParams.set('page', String(query.page));
  searchParams.set('limit', String(query.limit));
  searchParams.set('horizonWeeks', String(query.horizonWeeks));
  if (query.trialLaboratoryId !== undefined) {
    searchParams.set('trialLaboratoryId', String(query.trialLaboratoryId));
  }
  return searchParams;
}

function toBasketSearchParams(query: KitBasketQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  if (query.trialId !== undefined) {
    searchParams.set('trialId', String(query.trialId));
  }
  return searchParams;
}

function toOutProtocolsSearchParams(query: KitOutProtocolListQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(query.page));
  searchParams.set('limit', String(query.limit));
  if (query.trialLaboratoryId !== undefined) {
    searchParams.set('trialLaboratoryId', String(query.trialLaboratoryId));
  }
  return searchParams;
}

function toCorrectionsSearchParams(query: KitCorrectionListQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(query.page));
  searchParams.set('limit', String(query.limit));
  if (query.trialId !== undefined) {
    searchParams.set('trialId', String(query.trialId));
  }
  if (query.labKitId !== undefined) {
    searchParams.set('labKitId', String(query.labKitId));
  }
  return searchParams;
}

export const kitsApi = {
  async listLaboratories(
    query: TrialLaboratoryListQuery = { page: 1, limit: 100 },
  ): Promise<OffsetPaginated<TrialLaboratoryDto>> {
    const raw: unknown = await apiClient
      .get(API_PATHS.kits.laboratories, {
        searchParams: toLaboratoriesSearchParams(query),
      })
      .json();
    return TrialLaboratoryListResponseSchema.parse(raw);
  },

  async getLaboratory(id: number): Promise<TrialLaboratoryDto> {
    const raw: unknown = await apiClient.get(API_PATHS.kits.laboratory(id)).json();
    return TrialLaboratoryDtoSchema.parse(raw);
  },

  async createLaboratory(request: CreateTrialLaboratoryRequest): Promise<TrialLaboratoryDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.kits.laboratories, { json: request })
      .json();
    return TrialLaboratoryDtoSchema.parse(raw);
  },

  async updateLaboratory(
    id: number,
    request: UpdateTrialLaboratoryRequest,
  ): Promise<TrialLaboratoryDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.kits.laboratory(id), { json: request })
      .json();
    return TrialLaboratoryDtoSchema.parse(raw);
  },

  async listKits(
    query: LabKitListQuery = { page: 1, limit: 100 },
  ): Promise<OffsetPaginated<LabKitDto>> {
    const raw: unknown = await apiClient
      .get(API_PATHS.kits.list, {
        searchParams: toKitsSearchParams(query),
      })
      .json();
    return LabKitListResponseSchema.parse(raw);
  },

  async getKit(id: number): Promise<LabKitDto> {
    const raw: unknown = await apiClient.get(API_PATHS.kits.byId(id)).json();
    return LabKitDtoSchema.parse(raw);
  },

  async createKit(request: CreateLabKitRequest): Promise<LabKitDto> {
    const raw: unknown = await apiClient.post(API_PATHS.kits.create, { json: request }).json();
    return LabKitDtoSchema.parse(raw);
  },

  async updateKit(id: number, request: UpdateLabKitRequest): Promise<LabKitDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.kits.byId(id), { json: request })
      .json();
    return LabKitDtoSchema.parse(raw);
  },

  async listExpirations(kitId: number): Promise<LabKitExpirationDto[]> {
    const raw: unknown = await apiClient.get(API_PATHS.kits.expirations(kitId)).json();
    return LabKitExpirationListResponseSchema.parse(raw);
  },

  async createExpiration(
    kitId: number,
    request: CreateLabKitExpirationRequest,
  ): Promise<LabKitExpirationDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.kits.expirations(kitId), { json: request })
      .json();
    return LabKitExpirationDtoSchema.parse(raw);
  },

  async listShipments(
    query: KitShipmentListQuery,
  ): Promise<OffsetPaginated<KitShipmentDto>> {
    const raw: unknown = await apiClient
      .get(API_PATHS.kits.shipments, {
        searchParams: toShipmentsSearchParams(query),
      })
      .json();
    return KitShipmentListResponseSchema.parse(raw);
  },

  async getShipment(id: number): Promise<KitShipmentDto> {
    const raw: unknown = await apiClient.get(API_PATHS.kits.shipment(id)).json();
    return KitShipmentDtoSchema.parse(raw);
  },

  async createShipment(request: CreateKitShipmentRequest): Promise<KitShipmentDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.kits.shipments, { json: request })
      .json();
    return KitShipmentDtoSchema.parse(raw);
  },

  async getStock(query: KitStockQuery): Promise<KitStockItemDto[]> {
    const raw: unknown = await apiClient
      .get(API_PATHS.kits.stock, {
        searchParams: toStockSearchParams(query),
      })
      .json();
    return KitStockListResponseSchema.parse(raw);
  },

  /** REQ-KIT-027..029: kit demand forecast and shortage warnings for one trial. */
  async getDemandForecast(
    query: KitDemandForecastQuery,
  ): Promise<OffsetPaginated<KitDemandForecastItemDto>> {
    const raw: unknown = await apiClient
      .get(API_PATHS.kits.demandForecast, {
        searchParams: toDemandForecastSearchParams(query),
      })
      .json();
    return KitDemandForecastResponseSchema.parse(raw);
  },

  /** REQ-KIT-013..022: kits currently registered at one visit. */
  async getVisitKits(visitId: number): Promise<VisitKitsResponse> {
    const raw: unknown = await apiClient.get(API_PATHS.visits.kits(visitId)).json();
    return VisitKitsResponseSchema.parse(raw);
  },

  /** REQ-KIT-013..022: replaces the kits of one visit and sets its kit flag atomically. */
  async saveVisitKits(visitId: number, payload: SaveVisitKitsRequest): Promise<VisitKitsResponse> {
    const raw: unknown = await apiClient
      .put(API_PATHS.visits.kits(visitId), { json: payload })
      .json();
    return VisitKitsResponseSchema.parse(raw);
  },

  /** REQ-KIT-030..032: virtual basket of kits awaiting destruction. */
  async listBasket(query: KitBasketQuery = {}): Promise<KitBasketEntryDto[]> {
    const raw: unknown = await apiClient
      .get(API_PATHS.kits.out, {
        searchParams: toBasketSearchParams(query),
      })
      .json();
    return KitBasketListResponseSchema.parse(raw);
  },

  async addToBasket(request: AddKitOutRequest): Promise<KitOutDto> {
    const raw: unknown = await apiClient.post(API_PATHS.kits.out, { json: request }).json();
    return KitOutDtoSchema.parse(raw);
  },

  /** DELETE 204 — no body. 404 unknown item, 409 when already in a protocol. */
  async restoreFromBasket(id: number): Promise<void> {
    await apiClient.delete(API_PATHS.kits.outItem(id));
  },

  async listProtocols(
    query: KitOutProtocolListQuery,
  ): Promise<OffsetPaginated<KitOutProtocolWithItemsDto>> {
    const raw: unknown = await apiClient
      .get(API_PATHS.kits.outProtocols, {
        searchParams: toOutProtocolsSearchParams(query),
      })
      .json();
    return KitOutProtocolListResponseSchema.parse(raw);
  },

  async createProtocol(
    request: CreateKitOutProtocolRequest,
  ): Promise<KitOutProtocolWithItemsDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.kits.outProtocols, { json: request })
      .json();
    return KitOutProtocolWithItemsDtoSchema.parse(raw);
  },

  async getProtocol(id: number): Promise<KitOutProtocolWithItemsDto> {
    const raw: unknown = await apiClient.get(API_PATHS.kits.outProtocol(id)).json();
    return KitOutProtocolWithItemsDtoSchema.parse(raw);
  },

  /** REQ-KIT-033: signed stock corrections register. */
  async listCorrections(
    query: KitCorrectionListQuery,
  ): Promise<OffsetPaginated<KitCorrectionDto>> {
    const raw: unknown = await apiClient
      .get(API_PATHS.kits.corrections, {
        searchParams: toCorrectionsSearchParams(query),
      })
      .json();
    return KitCorrectionListResponseSchema.parse(raw);
  },

  async createCorrection(request: CreateKitCorrectionRequest): Promise<KitCorrectionDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.kits.corrections, { json: request })
      .json();
    return KitCorrectionDtoSchema.parse(raw);
  },
};
